/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { createBrowserClient } from "@/utils/supabase/client";
import { User } from "@supabase/supabase-js";
import config from "@/config";
import AuthenticatedNav from "./navigation/AuthenticatedNav";

interface ButtonSigninProps {
  text?: string;
  extraStyle?: string;
  showSignIn?: boolean;
}

// Enhanced button component that shows appropriate navigation based on auth state
// For logged-in users: Shows Dashboard button with user dropdown
// For logged-out users: Shows Sign In and Get Started buttons
const ButtonSignin: React.FC<ButtonSigninProps> = ({ 
  text = "Get started", 
  extraStyle,
  showSignIn = true 
}) => {
  const supabase = createBrowserClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUser = async () => {
      setIsLoading(true);
      const { data } = await supabase.auth.getUser();
      setUser(data.user);
      setIsLoading(false);
    };

    getUser();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`btn btn-ghost btn-disabled ${extraStyle ? extraStyle : ""}`}>
        <span className="loading loading-spinner loading-sm"></span>
      </div>
    );
  }

  // Show authenticated navigation
  if (user) {
    return <AuthenticatedNav user={user} extraStyle={extraStyle} />;
  }

  // Show sign in/get started buttons for non-authenticated users
  return (
    <div className="flex items-center gap-3">
      {showSignIn && (
        <Link
          className="btn btn-ghost btn-sm py-3 px-6 md:py-2 md:px-4 min-h-[44px] md:min-h-[36px]"
          href={config.auth.loginUrl}
        >
          Sign In
        </Link>
      )}
      <Link
        className={`btn bg-primary text-ink-on-brand hover:bg-budget-accent-dark hover:text-ink-on-brand py-3 px-6 md:py-2 md:px-4 text-base md:text-sm min-h-[44px] md:min-h-[36px] ${
          extraStyle ? extraStyle : ""
        }`}
        href={config.auth.loginUrl}
      >
        {text}
      </Link>
    </div>
  );
};

export default ButtonSignin;
